import { makeAutoObservable } from 'mobx';
import RootStore from 'stores/RootStore';
import {
  ChecklistResponseValue,
  DamageOutcome,
  DamageSeverity,
  DemoBatchTicket,
  DemoChecklist,
  DemoChecklistItem,
  DemoChecklistResponse,
  DemoCylinderBreak,
  DemoCylinderSample,
  DemoDamageIncident,
  DemoEngineeringRepair,
  DemoInspectionEvent,
  DemoPanelRejection,
  DemoScheduledInspection,
  DemoYardPiece,
  EngineeringRepairStatus,
  FailureCategory,
  InspectionStage,
  InspectionType,
  PassFail,
  YardStatus,
} from './qcDemoTypes';
import {
  seedBatchTickets,
  seedChecklists,
  seedCylinderBreaks,
  seedCylinderSamples,
  seedDamageIncidents,
  seedEngineeringRepairs,
  seedInspectionEvents,
  seedRejections,
  seedScheduledInspections,
  seedYardPieces,
} from './qcDemoSeed';

/**
 * In-memory demo store for the QC module UI prototype.
 *
 * Stands in for `qcInspectionsStore` / `panelRejectionsStore` ServiceStores
 * until the backend schema is finalized after the client demo. The shape of
 * `inspectionEvents` and `rejections` mirrors the TypeBox schemas in
 * IntraSync-API-Premier/src/services/quality/*, so swapping to real
 * ServiceStores should be a near-mechanical change.
 */
class QualityControlDemoStore {
  rootStore: RootStore;

  inspectionEvents: DemoInspectionEvent[] = [...seedInspectionEvents];
  rejections: DemoPanelRejection[] = [...seedRejections];
  scheduledInspections: DemoScheduledInspection[] = [...seedScheduledInspections];
  checklists: DemoChecklist[] = [...seedChecklists];
  checklistResponses: DemoChecklistResponse[] = [];
  batchTickets: DemoBatchTicket[] = [...seedBatchTickets];
  cylinderSamples: DemoCylinderSample[] = [...seedCylinderSamples];
  cylinderBreaks: DemoCylinderBreak[] = [...seedCylinderBreaks];
  engineeringRepairs: DemoEngineeringRepair[] = [...seedEngineeringRepairs];
  yardPieces: DemoYardPiece[] = [...seedYardPieces];
  damageIncidents: DemoDamageIncident[] = [...seedDamageIncidents];

  private _nextInspectionId = 2000;
  private _nextRejectionId = 6000;
  private _nextChecklistId = 100;
  private _nextChecklistItemId = 1000;
  private _nextChecklistResponseId = 10000;
  private _nextBatchTicketId = 8000;
  private _nextCylinderSampleId = 9000;
  private _nextCylinderBreakId = 9500;
  private _nextRepairId = 5000;
  private _nextYardId = 7000;
  private _nextDamageId = 8500;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  recordInspectionEvent = (input: {
    panelId: number;
    panelMark: string;
    projectId: number;
    projectName: string;
    inspectionStage: InspectionStage;
    inspectionType: InspectionType;
    passFail: PassFail;
    durationMinutes?: number;
    failureCategory?: FailureCategory;
    defectsNoted?: string;
    correctiveAction?: string;
    correctiveActionRequired?: boolean;
    reInspectionRequired?: boolean;
    approvedForNextStage?: boolean;
    inspectorId?: number;
    inspectorName?: string;
    notes?: string;
  }): DemoInspectionEvent => {
    const now = new Date().toISOString();
    const evt: DemoInspectionEvent = {
      inspectionId: this._nextInspectionId++,
      inspectionDatetime: now,
      createdAt: now,
      updatedAt: now,
      ...input,
    };
    this.inspectionEvents.unshift(evt);
    this.updateScheduledStatusForEvent(evt);
    return evt;
  };

  recordRejection = (input: {
    panelId: number;
    panelMark: string;
    projectId: number;
    projectName: string;
    qcInspectionId?: number;
    reason: string;
    failureCategory?: FailureCategory;
    reportedBy?: number;
    reportedByName?: string;
    notes?: string;
  }): DemoPanelRejection => {
    const now = new Date().toISOString();
    const rej: DemoPanelRejection = {
      rejectionId: this._nextRejectionId++,
      status: 'open',
      reportedAt: now,
      createdAt: now,
      updatedAt: now,
      ...input,
    };
    this.rejections.unshift(rej);

    const sched = this.scheduledInspections.find((s) => s.panelId === input.panelId);
    if (sched) sched.qcStatus = 'Red';

    return rej;
  };

  approveRejectionReschedule = (
    rejectionId: number,
    input: { newDate: string; approvedBy?: number; approvedByName?: string; notes?: string }
  ): void => {
    const rej = this.rejections.find((r) => r.rejectionId === rejectionId);
    if (!rej) return;
    rej.status = 'rescheduled';
    rej.rescheduledNewDate = input.newDate;
    rej.rescheduledApprovedBy = input.approvedBy;
    rej.rescheduledApprovedByName = input.approvedByName;
    rej.rescheduledApprovedAt = new Date().toISOString();
    if (input.notes) rej.notes = input.notes;
    rej.updatedAt = new Date().toISOString();
  };

  dismissRejection = (
    rejectionId: number,
    input: { dismissedBy?: number; dismissedByName?: string; notes?: string }
  ): void => {
    const rej = this.rejections.find((r) => r.rejectionId === rejectionId);
    if (!rej) return;
    rej.status = 'dismissed';
    rej.dismissedAt = new Date().toISOString();
    rej.dismissedBy = input.dismissedBy;
    rej.dismissedByName = input.dismissedByName;
    if (input.notes) rej.notes = input.notes;
    rej.updatedAt = new Date().toISOString();
  };

  private updateScheduledStatusForEvent(evt: DemoInspectionEvent): void {
    const sched = this.scheduledInspections.find((s) => s.panelId === evt.panelId);
    if (!sched) return;
    sched.lastInspectionId = evt.inspectionId;

    if (evt.passFail === 'fail') {
      // Red status is set explicitly by the rejection flow; for non-postpour
      // fails we still mark the row as in-progress so it stays visible.
      sched.qcStatus = 'InProgress';
      return;
    }
    if (evt.inspectionStage === 'pre_casting') sched.qcStatus = 'PrePourCompleted';
    else if (evt.inspectionStage === 'during_casting') sched.qcStatus = 'Poured';
    else if (evt.inspectionStage === 'post_stripping') sched.qcStatus = 'White';
    else if (evt.inspectionStage === 'post_finishing') sched.qcStatus = 'Yellow';
    else if (evt.inspectionStage === 'pre_shipping') sched.qcStatus = 'Green';
  }

  get todaysScheduled(): DemoScheduledInspection[] {
    const today = new Date().toISOString().slice(0, 10);
    return this.scheduledInspections.filter((s) => s.scheduledDate === today);
  }

  get todaysEvents(): DemoInspectionEvent[] {
    const today = new Date().toISOString().slice(0, 10);
    return this.inspectionEvents.filter((e) => e.inspectionDatetime.slice(0, 10) === today);
  }

  get openRejections(): DemoPanelRejection[] {
    return this.rejections.filter((r) => r.status === 'open');
  }

  get rescheduledRejections(): DemoPanelRejection[] {
    return this.rejections.filter((r) => r.status === 'rescheduled');
  }

  get dismissedRejections(): DemoPanelRejection[] {
    return this.rejections.filter((r) => r.status === 'dismissed');
  }

  lastEventForPanel = (panelId: number, stage?: InspectionStage): DemoInspectionEvent | undefined => {
    const filtered = this.inspectionEvents.filter(
      (e) => e.panelId === panelId && (stage ? e.inspectionStage === stage : true)
    );
    return filtered[0];
  };

  // ==========================================================================
  // Checklists
  // ==========================================================================

  checklistForStage = (stage: InspectionStage): DemoChecklist | undefined => {
    return this.checklists.find((c) => c.stage === stage && c.active);
  };

  upsertChecklist = (input: {
    checklistId?: number;
    name: string;
    stage: InspectionStage;
    description?: string;
    items: { itemId?: number; text: string; required: boolean; allowsNA: boolean }[];
    active?: boolean;
  }): DemoChecklist => {
    const now = new Date().toISOString();
    const items: DemoChecklistItem[] = input.items.map((it) => ({
      itemId: it.itemId ?? this._nextChecklistItemId++,
      text: it.text,
      required: it.required,
      allowsNA: it.allowsNA,
    }));

    if (input.checklistId) {
      const existing = this.checklists.find((c) => c.checklistId === input.checklistId);
      if (existing) {
        existing.name = input.name;
        existing.stage = input.stage;
        existing.description = input.description;
        existing.items = items;
        existing.active = input.active ?? existing.active;
        existing.updatedAt = now;
        return existing;
      }
    }
    const created: DemoChecklist = {
      checklistId: this._nextChecklistId++,
      name: input.name,
      stage: input.stage,
      description: input.description,
      items,
      active: input.active ?? true,
      updatedAt: now,
    };
    this.checklists.unshift(created);
    return created;
  };

  deleteChecklist = (checklistId: number): void => {
    this.checklists = this.checklists.filter((c) => c.checklistId !== checklistId);
  };

  toggleChecklistActive = (checklistId: number): void => {
    const c = this.checklists.find((x) => x.checklistId === checklistId);
    if (c) {
      c.active = !c.active;
      c.updatedAt = new Date().toISOString();
    }
  };

  recordChecklistResponse = (input: {
    checklistId: number;
    inspectionId: number;
    itemResponses: { itemId: number; value: ChecklistResponseValue; notes?: string }[];
  }): DemoChecklistResponse => {
    const resp: DemoChecklistResponse = {
      responseId: this._nextChecklistResponseId++,
      checklistId: input.checklistId,
      inspectionId: input.inspectionId,
      itemResponses: input.itemResponses,
      createdAt: new Date().toISOString(),
    };
    this.checklistResponses.unshift(resp);
    return resp;
  };

  // ==========================================================================
  // Concrete testing
  // ==========================================================================

  recordBatchTicket = (input: Omit<DemoBatchTicket, 'batchTicketId'>): DemoBatchTicket => {
    const created: DemoBatchTicket = {
      ...input,
      batchTicketId: this._nextBatchTicketId++,
    };
    this.batchTickets.unshift(created);
    return created;
  };

  recordCylinderSample = (input: Omit<DemoCylinderSample, 'sampleId'>): DemoCylinderSample => {
    const created: DemoCylinderSample = {
      ...input,
      sampleId: this._nextCylinderSampleId++,
    };
    this.cylinderSamples.unshift(created);
    return created;
  };

  scheduleCylinderBreak = (input: Omit<DemoCylinderBreak, 'breakId'>): DemoCylinderBreak => {
    const created: DemoCylinderBreak = {
      ...input,
      breakId: this._nextCylinderBreakId++,
    };
    this.cylinderBreaks.unshift(created);
    return created;
  };

  recordCylinderBreakResult = (
    breakId: number,
    input: { strengthPsi: number; brokenByName?: string; notes?: string }
  ): void => {
    const b = this.cylinderBreaks.find((x) => x.breakId === breakId);
    if (!b) return;
    const sample = this.cylinderSamples.find((s) => s.sampleId === b.sampleId);
    const batch = sample ? this.batchTickets.find((bt) => bt.batchTicketId === sample.batchTicketId) : undefined;
    const target = batch?.targetStrengthPsi ?? input.strengthPsi;
    b.strengthPsi = input.strengthPsi;
    b.percentOfTarget = Math.round((input.strengthPsi / target) * 1000) / 10;
    b.passFail = input.strengthPsi >= target ? 'pass' : 'fail';
    b.brokenAt = new Date().toISOString();
    b.brokenByName = input.brokenByName;
    b.notes = input.notes;
  };

  batchTicketsForPanel = (panelId: number): DemoBatchTicket[] => {
    return this.batchTickets.filter((b) => b.linkedPanelIds.includes(panelId));
  };

  get todaysBatchTickets(): DemoBatchTicket[] {
    const today = new Date().toISOString().slice(0, 10);
    return this.batchTickets.filter((b) => b.pouredAt.slice(0, 10) === today);
  }

  get pendingCylinderBreaks(): DemoCylinderBreak[] {
    return this.cylinderBreaks.filter((b) => !b.brokenAt);
  }

  get overdueCylinderBreaks(): DemoCylinderBreak[] {
    const today = new Date().toISOString().slice(0, 10);
    return this.cylinderBreaks.filter((b) => !b.brokenAt && b.scheduledDate < today);
  }

  // ==========================================================================
  // Engineering Repair (NCR)
  // ==========================================================================

  sendRejectionToEngineering = (
    rejectionId: number,
    input: { sentByName?: string; notes?: string }
  ): DemoEngineeringRepair | undefined => {
    const rej = this.rejections.find((r) => r.rejectionId === rejectionId);
    if (!rej) return undefined;
    rej.status = 'rescheduled';
    rej.notes = input.notes ? `${rej.notes ? rej.notes + ' · ' : ''}${input.notes}` : rej.notes;
    rej.updatedAt = new Date().toISOString();
    const created: DemoEngineeringRepair = {
      repairId: this._nextRepairId++,
      rejectionId,
      panelId: rej.panelId,
      panelMark: rej.panelMark,
      projectId: rej.projectId,
      projectName: rej.projectName,
      status: 'awaiting_design',
      sentToEngineeringAt: new Date().toISOString(),
      sentByName: input.sentByName,
      notes: input.notes,
    };
    this.engineeringRepairs.unshift(created);
    return created;
  };

  recordRepairPlan = (
    repairId: number,
    input: { repairPlan: string; repairPlanByName?: string }
  ): void => {
    const r = this.engineeringRepairs.find((x) => x.repairId === repairId);
    if (!r) return;
    r.repairPlan = input.repairPlan;
    r.repairPlanByName = input.repairPlanByName;
    r.repairPlanAt = new Date().toISOString();
    r.status = 'repair_in_progress';
    r.repairStartedAt = new Date().toISOString();
  };

  markRepairCompleted = (
    repairId: number,
    input: { repairCompletedByName?: string; notes?: string }
  ): void => {
    const r = this.engineeringRepairs.find((x) => x.repairId === repairId);
    if (!r) return;
    r.status = 'awaiting_reinspection';
    r.repairCompletedAt = new Date().toISOString();
    r.repairCompletedByName = input.repairCompletedByName;
    if (input.notes) r.notes = input.notes;
  };

  closeRepair = (
    repairId: number,
    input: { closedByName?: string; passed: boolean; notes?: string }
  ): void => {
    const r = this.engineeringRepairs.find((x) => x.repairId === repairId);
    if (!r) return;
    r.status = input.passed ? 'closed_repaired' : 'closed_rejected';
    r.closedAt = new Date().toISOString();
    r.closedByName = input.closedByName;
    if (input.notes) r.notes = input.notes;
    if (!input.passed) r.rejectedReason = input.notes;
  };

  repairsByStatus = (status: EngineeringRepairStatus): DemoEngineeringRepair[] => {
    return this.engineeringRepairs.filter((r) => r.status === status);
  };

  get openEngineeringRepairs(): DemoEngineeringRepair[] {
    return this.engineeringRepairs.filter(
      (r) => r.status !== 'closed_repaired' && r.status !== 'closed_rejected'
    );
  }

  // ==========================================================================
  // Yard QC
  // ==========================================================================

  setYardStatus = (yardId: number, status: YardStatus): void => {
    const p = this.yardPieces.find((y) => y.yardId === yardId);
    if (p) p.status = status;
  };

  recordDamageIncident = (
    input: Omit<DemoDamageIncident, 'incidentId' | 'reportedAt'>
  ): DemoDamageIncident => {
    const created: DemoDamageIncident = {
      ...input,
      incidentId: this._nextDamageId++,
      reportedAt: new Date().toISOString(),
    };
    this.damageIncidents.unshift(created);
    const piece = this.yardPieces.find((y) => y.panelId === input.panelId);
    if (piece) piece.status = 'damaged';
    return created;
  };

  yardPiecesByStatus = (status: YardStatus): DemoYardPiece[] => {
    return this.yardPieces.filter((p) => p.status === status);
  };

  damageIncidentForPanel = (panelId: number): DemoDamageIncident | undefined => {
    return this.damageIncidents.find((d) => d.panelId === panelId);
  };

  get readyToShip(): DemoYardPiece[] { return this.yardPiecesByStatus('ready_to_ship'); }
  get pendingFinalCheck(): DemoYardPiece[] { return this.yardPiecesByStatus('pending_final_check'); }
  get damagedInYard(): DemoYardPiece[] { return this.yardPiecesByStatus('damaged'); }

  // Avoid unused-import flag for severity/outcome types — they're consumed via the input types
  // of recordDamageIncident
  private _typeAnchor?: { sev: DamageSeverity; out: DamageOutcome };
}

export default QualityControlDemoStore;
